import React, { ReactElement, ReactNode } from "react";

import cn from "classnames";
import { useIntl } from "react-intl";

import User from "@mapmycustomers/shared/types/User";
import { Button, type ButtonProps } from "@mapmycustomers/ui";

import RoundButton from "@app/component/RoundButton";
import ButtonType from "@app/enum/ButtonType";

import messages from "../messages";

import styles from "./Footer.module.scss";

export type RenderApplyButton = (
  applyButton: ReactElement<ButtonProps>,
  context: { onApply?: () => void; selectedUserIds: User["id"][] }
) => ReactNode;

interface Props {
  allowEmpty?: boolean;
  cancelButton?: ReactNode;
  className?: string;
  confirmText?: string;
  extraButton?: ReactNode;
  onApply?: () => void;
  onCancel?: () => void;
  onDeselectAll?: () => void;
  onSelectAll?: () => void;
  renderApplyButton?: RenderApplyButton;
  selectedUserIds: User["id"][];
  users: User[];
}

const Footer = ({
  allowEmpty,
  cancelButton,
  className,
  confirmText,
  extraButton,
  onApply,
  onCancel,
  onDeselectAll,
  onSelectAll,
  renderApplyButton,
  selectedUserIds,
  users,
}: Props) => {
  const intl = useIntl();

  const applyButton: ReactElement<ButtonProps> = (
    <Button disabled={!selectedUserIds.length && !allowEmpty} type="primary">
      {confirmText ?? intl.formatMessage(messages.apply)}
    </Button>
  );

  return (
    <div className={cn(styles.container, className)}>
      <div className={styles.selectionInfo}>
        {intl.formatMessage(messages.selectionInfo, {
          count: selectedUserIds.length,
          total: users.length,
        })}
        {selectedUserIds.length > 0 && (
          <RoundButton
            bordered={false}
            className={styles.toggleSelectionButton}
            onClick={onDeselectAll}
            size="small"
            type={ButtonType.SECONDARY}
          >
            {intl.formatMessage(messages.deselectAll)}
          </RoundButton>
        )}
        {selectedUserIds.length < users.length && (
          <RoundButton
            bordered={false}
            className={styles.toggleSelectionButton}
            onClick={onSelectAll}
            size="small"
            type={ButtonType.PRIMARY}
          >
            {intl.formatMessage(messages.selectAll)}
          </RoundButton>
        )}
      </div>
      <div className={styles.buttons}>
        {cancelButton || <Button onClick={onCancel}>{intl.formatMessage(messages.cancel)}</Button>}
        {extraButton}
        {renderApplyButton
          ? renderApplyButton(applyButton, { onApply, selectedUserIds })
          : React.cloneElement(applyButton, { onClick: onApply })}
      </div>
    </div>
  );
};

export default Footer;
